import React, { createContext, useContext, useState, useEffect } from 'react';
import pb from '../lib/pocketbase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (pb.authStore.isValid) {
      setUser(pb.authStore.model);
    }
    setLoading(false);

    const unsubscribe = pb.authStore.onChange((token, model) => {
      setUser(model);
    });

    return unsubscribe;
  }, []);

  // Realtime User Updates (e.g. Role Promotion)
  useEffect(() => {
    let unsubCallback;

    const initRealtime = async () => {
      if (!user?.id) return;

      try {
        // Subscribe to changes on the specific user record
        unsubCallback = await pb.collection('users').subscribe(user.id, async (e) => {
          if (e.action === 'update') {
            console.log("Mise à jour utilisateur temps réel :", e.record);
            // Refresh the auth token/model to apply changes (e.g. role update)
            await pb.collection('users').authRefresh();
          }
        });
      } catch (err) {
        console.warn("Auth realtime error:", err);
      }
    };

    initRealtime();

    return () => {
      if (user?.id && pb.realtime.isConnected) {
        pb.collection('users').unsubscribe(user.id).catch(() => { });
      }
    };
  }, [user?.id]);

  const login = async (email, password) => {
    try {
      const authData = await pb.collection('users').authWithPassword(email, password);
      setUser(authData.record);
      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message || 'Erreur de connexion' };
    }
  };

  const register = async (email, password, passwordConfirm, name) => {
    try {
      const data = { email, password, passwordConfirm, name, emailVisibility: true };
      await pb.collection('users').create(data);
      await pb.collection('users').requestVerification(email);
      return { success: true, message: 'Compte créé. Vérifiez votre email.' };
    } catch (error) {
      return { success: false, error: error?.message || "Erreur d'inscription" };
    }
  };

  const logout = () => {
    pb.authStore.clear();
    setUser(null);
  };

  const value = { user, loading, login, register, logout, isAuthenticated: !!user, isAdmin: user?.role === 'admin', isCommercial: user?.role === 'commercial' };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
